import "./index.scss";
import { useForm } from "react-hook-form";
import cn from "classnames";
import Input from "../../shared/Input";
import PhoneInput from "../../shared/InputMask";
import Checkbox from "../../shared/Checkbox";
import Button from "../../shared/Button";
import OtpInput from "../../shared/OtpInput";
import RadioGroup from "../../shared/RadioGroup";
import Select from "../../shared/Select";
import React, { ReactNode, useCallback } from "react";
import { debounce } from "../../app/utils/debounce";
import { getData } from "../../app/utils/api";

interface IForm {
  security?: string;
  data: {
    success: 0 | 1;
    message: string;
    api_request: string;
    redirect_url?: string;
    title: string;
    depends_on_value: {
      [key: string]: {
        [key: string]: string;
      };
    };
    form_structure: {
      [key: string]: {
        name: string;
        type: string;
        value: string | number;
        required: boolean;
      };
    };
    show_menu: boolean;
    user_menu: any;
    buttons: {
      button_redirect: {
        name: string;
        label: string;
        redirect_url: string;
      };
      button_next: {
        name: string;
        label: string;
        api_url: string;
      };
      button_back: {
        name: string;
        label: string;
        api_url: string;
      };
    };
  };
  children?: ReactNode;
  formError?: string;
  validationType?:
    | "onBlur"
    | "onChange"
    | "onSubmit"
    | "onTouched"
    | "all"
    | undefined;
  onBack: (url: string) => void;
  onSubmit: (data: any) => void;
  onError?: (data: any) => void;
}

const Form = ({
  security,
  data,
  onSubmit,
  onError,
  onBack,
  children,
  formError,
  validationType = "onChange",
}: IForm) => {
  const isWidgetBehavior = !data?.show_menu;
  const buttons = data.buttons || {};
  const dependsOn = data.depends_on_value || {};

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    getValues,
  } = useForm({
    mode: validationType,
  });
  const buttonsDisabled = validationType !== "onSubmit" && !isValid;

  const getFieldChainedValues = debounce(
    async ({ name, value, requestUrl }: any) => {
      if (!security) return;
      try {
        const data = await getData({
          url: requestUrl,
          method: "POST",
          data: {
            security,
            [name]: value,
          },
        });

        Object.entries(data).forEach(([key, value]) => {
          setValue(key, value);
        });
      } catch (e) {}
    },
    200
  );

    // вытаскивает сообщение ошибки по точному name
    const getErrorForName = (errs: any, fieldName: string): string => {
        if (!errs || !fieldName) return '';

        // 1) прямой ключ (если ошибки кладутся как 'contacts__legal_act')
        const direct = errs?.[fieldName];
        if (typeof direct === 'string') return direct;
        if (direct && typeof direct.message === 'string') return direct.message;

        // 2) массивная нотация: 'founders__full_name[11]' -> base='founders__full_name', idx=11
        const m = fieldName.match(/^(.*)\[(\d+)\]$/);
        if (m) {
            const base = m[1];
            const idx = Number(m[2]);
            const nested = errs?.[base]?.[idx];
            if (typeof nested === 'string') return nested;
            if (nested && typeof nested.message === 'string') return nested.message;
        }

        return '';
    };

  const innerFieldContent = useCallback(
    (input: any) => {
      const {
        name,
        type,
        value,
        required,
        pattern,
        mask,
        placeholder,
        maxlength,
        label: initLabel,
        hint,
        length,
        readonly,
        requestUrl,
        helper_text,
        del_url,
        save_url,
        save_title
      } = input;

      if (type === "title") {
        return (
          <span className="form__group-title" key={initLabel}>
            {initLabel}
          </span>
        );
      }

      if (type === "note") {
        return (
          <span className="form__note" dangerouslySetInnerHTML={{ __html: initLabel }}>
          </span>
        );
      }

      let options: any = {};

      if (required) {
        options["required"] = {
          value: required,
          message: "Поле обязательное к заполнению",
        };
      }
      if (pattern) {
        options["pattern"] = {
          value: new RegExp(pattern),
          message: "Неверный формат. Исправьте и попробуйте снова.",
        };
      }

      let activeOption: any;
      let selectOptions: any = [];

      if (type === "select" || type === "listbox") {
        selectOptions = Object.entries(input.options || []).map(
          ([val, text]: any) => {
            const option = {
              value: val,
              text,
            };
            if (val === value) {
              activeOption = option;
            }
            return option;
          }
        );

        if (activeOption?.value) {
          options.value = activeOption.value;
        }
      } else {
        options.value = getValues()?.hasOwnProperty(name)
          ? getValues(name)
          : value;
      }

      const error = errors[name]?.message as string;

      const registeredParams = register(name, options);

      const label = initLabel ? `${initLabel}${required ? "*" : ""}` : "";

      switch (type) {
          case "button-group-del-save":
              return (
                  <div className="form__buttons form__buttons__del-and-save">
                      {del_url && (<Button
                              className='button_secondary'
                              {...{
                                  type: "button",
                                  onClick: () => {
                                      onBack(del_url);
                                  },
                              }}
                          >
                              Удалить
                          </Button>
                      )}
                      <Button
                          className='form__submit'
                          {...{
                              type: "button",
                              disabled: buttonsDisabled,
                              onClick: (e: any) => {
                                  handleSubmit((form) => {
                                      onSubmit({
                                          form,
                                          url: save_url,
                                      });
                                  }, onError)();
                              },
                          }}
                      >
                          {save_title}
                      </Button>
                  </div>
              );
        case "hidden":
          return <React.Fragment key={name}></React.Fragment>;
        // return (
        //   <input
        //     key={name}
        //     defaultValue={value}
        //     type="text"
        //     className="hidden-accessible"
        //     placeholder={placeholder}
        //     {...registeredParams}
        //   />
        // );
        case "checkbox":
        case "check-box":
          return (
            <Checkbox
              key={name}
              label={label}
              error={error}
              readonly={readonly}
              likePopup={isWidgetBehavior}
              {...registeredParams}
              onChange={(e) => {
                registeredParams.onChange(e);

                try {
                  const fields = dependsOn?.[name]?.[Number(e.target.checked)];

                  Object.entries(fields).forEach(([copy, paste]) => {
                    setValue(paste, getValues(copy));
                  });
                } catch (e) {}
              }}
            />
          );

        case "otp":
          return (
            <OtpInput
              key={name}
              label={label}
              hint={hint}
              error={error}
              length={length}
              likePopup={isWidgetBehavior}
              readonly={readonly}
              onChange={(value) => {
                setValue(name, value, { shouldValidate: !!value });
              }}
              onBlur={(value) => {
                setValue(name, value, { shouldValidate: !!required });
              }}
            />
          );
        case "radio":
          return (
            <RadioGroup
              key={name}
              name={name}
              options={input.options}
              label={label}
              hint={hint}
              error={error}
              value={value}
              readonly={readonly}
              helper_text={helper_text}
              onChange={(value: string) => {
                setValue(name, value, { shouldValidate: true });
              }}
            />
          );
        case "select":
        case "listbox":
          return (
            <Select
              key={name}
              name={name}
              placeholder={placeholder}
              label={label}
              value={activeOption}
              options={selectOptions!}
              hint={hint}
              error={error}
              readonly={readonly}
              likePopup={isWidgetBehavior}
              onChange={(value: string) => {
                setValue(name, value, { shouldValidate: true });
              }}
            />
          );
        case "input":
        default:
          if (mask) {
            //@ts-ignore
            return (
              <PhoneInput
                key={name}
                label={label}
                hint={hint}
                error={error}
                placeholder={mask}
                likePopup={isWidgetBehavior}
                mask={mask}
                defaultValue={value}
                readonly={readonly}
                onChange={(value) => {
                  setValue(name, value, { shouldValidate: !!value });
                }}
                onBlur={(value) => {
                  setValue(name, value, { shouldValidate: !!required });
                }}
              />
            );
          } else {
            return (
              <Input
                key={name}
                placeholder={placeholder}
                maxlength={maxlength}
                type={type}
                label={label}
                hint={hint}
                error={getErrorForName(errors, name)}
                value={options.value}
                readonly={readonly}
                likePopup={isWidgetBehavior}
                {...registeredParams}
                onChange={(e) => {
                  registeredParams.onChange(e);

                  if (requestUrl) {
                    getFieldChainedValues({
                      name,
                      value: e.target.value,
                      requestUrl,
                    });
                  }
                }}
              />
            );
          }
      }
    },
    [errors]
  );

  return (
    <section className={cn("form", { form_popup: isWidgetBehavior })}>
      <div className="form__container">
        <form
          className="form__content"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          {data.title && <h1 className="form__title">{data.title}</h1>}
          {data.message && (
            <div
              className="form__description"
              dangerouslySetInnerHTML={{ __html: data.message }}
            ></div>
          )}

          <div className="form__inputs">
            {Object.values(data?.form_structure || {}).map(innerFieldContent)}
          </div>
          {children}
          <div className="form__buttons">
            {data.redirect_url ? (
              <a className="button" href={data.redirect_url}>
                Продолжить
              </a>
            ) : (
              <>
                {buttons.button_back && (
                  <Button
                    className={cn("button_secondary", {
                      disabled: buttonsDisabled,
                    })}
                    {...{
                      type: "button",
                      onClick: () => {
                        onBack(buttons.button_back.api_url);
                      },
                    }}
                  >
                    {buttons.button_back.label}
                  </Button>
                )}
                {buttons.button_redirect && (
                  <Button
                    className="button_secondary"
                    {...{
                      type: "button",
                      onClick: () => {
                        window.location.href = buttons.button_redirect.redirect_url
                      },
                    }}
                  >
                    {buttons.button_redirect.label}
                  </Button>
                )}
                {buttons.button_next && (
                  <Button
                    className={cn("form__submit", {
                      disabled: buttonsDisabled,
                      "button__width_by_content": buttons.button_redirect,
                    })}
                    {...{
                      type: "button",
                      disabled: buttonsDisabled,
                      onClick: (e: any) => {
                        handleSubmit((form) => {
                          onSubmit({
                            form,
                            url: buttons.button_next.api_url,
                          });
                        }, onError)();
                      },
                    }}
                  >
                    {buttons.button_next.label}
                  </Button>
                )}
              </>
            )}
          </div>
          {formError && (
            <span
              className="form__error"
              dangerouslySetInnerHTML={{ __html: formError }}
            />
          )}
        </form>
      </div>
    </section>
  );
};

export default Form;
